import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeSlashIcon, EyeIcon } from '@heroicons/react/24/outline';

/**
 * UserCard component for displaying user profiles in the network directory
 * Displays profile picture, name, role, company, location, and job search status
 * Includes admin functionality for hiding/unhiding users
 */
const UserCard = ({ 
  user, 
  isAdmin = false, 
  onHideUser = null, 
  onUnhideUser = null, 
  isHidden = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Extract user data with fallbacks
  const {
    uid,
    basicInfo = {},
    preferences = {}
  } = user || {};

  const {
    firstName = '',
    lastName = '',
    currentRole = '',
    companyName = '',
    location = '',
    photoURL = null
  } = basicInfo;

  const {
    jobSearchStatus = 'not_looking'
  } = preferences;

  // Handle card click to navigate to user profile
  const handleCardClick = () => {
    if (uid) {
      navigate(`/network/${uid}`);
    }
  };

  // Handle hide/unhide actions
  const handleHideAction = async (e) => {
    e.stopPropagation(); // Prevent card click events
    
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      if (isHidden && onUnhideUser) {
        await onUnhideUser(uid);
      } else if (!isHidden && onHideUser) {
        await onHideUser(uid);
      }
    } catch (error) {
      console.error('Error in hide/unhide action:', error);
      // Error handling will be managed by parent component
    } finally {
      setIsLoading(false);
    }
  };

  // Generate initials for fallback avatar
  const getInitials = () => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial || '?';
  };

  // Get job search status badge configuration
  const getStatusBadge = () => {
    switch (jobSearchStatus) {
      case 'actively_looking':
        return {
          text: 'Actively Looking',
          className: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'open_to_opportunities':
        return {
          text: 'Open to Opportunities',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'not_looking':
        return {
          text: 'Not Looking',
          className: 'bg-gray-100 text-gray-600 border-gray-200'
        };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge();
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <div 
      onClick={handleCardClick}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 
                    p-4 sm:p-6 h-full flex flex-col relative
                    hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 
                    hover:border-gray-300 dark:hover:border-gray-600 
                    hover:-translate-y-1 hover:scale-[1.02]
                    transition-all duration-300 ease-out group
                    cursor-pointer
                    focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 
                    dark:focus-within:ring-offset-gray-900
                    ${isHidden ? 'opacity-75 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/10' : ''}`}>
      
      {/* Admin Hide/Unhide Button */}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={handleHideAction}
            disabled={isLoading}
            className={`p-2 rounded-full transition-all duration-200 
                       hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 
                       ${isHidden 
                         ? 'bg-green-100 hover:bg-green-200 text-green-600 focus:ring-green-500 dark:bg-green-900/30 dark:hover:bg-green-800/50 dark:text-green-400' 
                         : 'bg-red-100 hover:bg-red-200 text-red-600 focus:ring-red-500 dark:bg-red-900/30 dark:hover:bg-red-800/50 dark:text-red-400'
                       }
                       ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
            title={isHidden ? 'Unhide user' : 'Hide user'}
          >
            {isLoading ? (
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
            ) : isHidden ? (
              <EyeIcon className="w-4 h-4" />
            ) : (
              <EyeSlashIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* Profile Picture */}
      <div className="flex justify-center mb-4">
        {photoURL ? (
          <img
            src={photoURL}
            alt={`${fullName}'s profile`}
            className="w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 rounded-full object-cover 
                     border-2 border-gray-200 dark:border-gray-600 
                     group-hover:border-purple-400 dark:group-hover:border-purple-500
                     transition-all duration-300 ease-out
                     shadow-md group-hover:shadow-lg"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Fallback avatar with initials */}
        <div 
          className={`w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 rounded-full 
                     bg-white dark:bg-gray-800 
                     border-2 border-gray-200 dark:border-gray-600 
                     group-hover:border-purple-400 dark:group-hover:border-purple-500
                     flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold 
                     text-lg sm:text-xl lg:text-2xl
                     transition-all duration-300 ease-out
                     shadow-md group-hover:shadow-lg ${
            photoURL ? 'hidden' : 'flex'
          }`}
        >
          {getInitials()}
        </div>
      </div>

      {/* User Information - flex-grow to push status badge to bottom */}
      <div className="text-center space-y-2 flex-grow flex flex-col">
        {/* Name */}
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white 
                     group-hover:text-purple-600 dark:group-hover:text-purple-400 
                     transition-colors duration-300 ease-out
                     h-[3rem] flex items-center justify-center overflow-hidden">
          <span className="line-clamp-2 text-center leading-tight">
            {fullName || 'Unknown User'}
          </span>
        </h3>

        {/* Role */}
        <div className="h-[1.5rem] flex items-center justify-center">
          {currentRole && (
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300
                        group-hover:text-gray-700 dark:group-hover:text-gray-200
                        transition-colors duration-300 ease-out
                        truncate max-w-full px-2">
              {currentRole}
            </p>
          )}
        </div>

        {/* Company */}
        <div className="h-[1.5rem] flex items-center justify-center">
          {companyName && (
            <p className="text-sm text-gray-500 dark:text-gray-400 
                        group-hover:text-gray-600 dark:group-hover:text-gray-300
                        transition-colors duration-300 ease-out
                        flex items-center justify-center max-w-full px-2">
              <svg 
                className="w-4 h-4 mr-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity duration-300" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{companyName}</span>
            </p>
          )}
        </div>

        {/* Location */}
        <div className="h-[1.5rem] flex items-center justify-center">
          {location && (
            <p className="text-sm text-gray-500 dark:text-gray-400 
                        group-hover:text-gray-600 dark:group-hover:text-gray-300
                        transition-colors duration-300 ease-out
                        flex items-center justify-center max-w-full px-2">
              <svg 
                className="w-4 h-4 mr-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity duration-300" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{location}</span>
            </p>
          )}
        </div>

        {/* Spacer to push status badge to bottom */}
        <div className="flex-grow"></div>

        {/* Job Search Status Badge */}
        <div className="pt-3 mt-auto">
          {statusBadge && (
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border 
                           transition-all duration-300 ease-out
                           group-hover:scale-105 group-hover:shadow-sm ${statusBadge.className}
                           ${statusBadge.className.includes('green') ? 'dark:bg-green-900/30 dark:text-green-300 dark:border-green-700' : ''}
                           ${statusBadge.className.includes('yellow') ? 'dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700' : ''}
                           ${statusBadge.className.includes('gray') ? 'dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600' : ''}`}>
              {statusBadge.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCard;